// types
import type { NormalizedError } from './fsSyncFailure';
import type { CatalogAccessory } from '@makemydeal/dr-dash-types';
import type { IDealerFee } from '@makemydeal/dr-platform-shared';
import type { FeeOverride, ItemizedGovernmentFee, PDfPaymentGrid, paymentServicesTypes } from '@makemydeal/dr-platform-types';
import type { FeeCategories } from '@makemydeal/dr-shared-types';

export type ScopeGridSelection = Required<Pick<PDfPaymentGrid, 'selectedTermIndices' | 'selectedDownPaymentIndices'>>;
export type ScopeGridSelections = Record<string, ScopeGridSelection>;

export type SharingType = 'text' | 'email';
export type ForSharing = false | SharingType;

export type BreakdownItem = {
    name: string;
    value: number;
};

export type Lead = {
    id?: string;
    hash?: string;
    timestamp?: number;
    url?: string | null;
};

export type CreditTierScoreHighLow = {
    high: number;
    low: number;
};

export type GetAllQuotesOptions =
    | { quoteAllCreditTiers: true }
    | { keptLenderLookup: string }
    | { creditScoreOverride?: number; requestedTier?: string; primary?: boolean };

export type UserConsent = 'unchecked' | 'checked';

export type OfferLenderDecisionMocked = {
    success: boolean;
    message: string;
    body: OfferLenderDecisionPayload;
};

export type OfferLenderDecisionPayload = {
    approvalStatus: string;
    approvedAmount: number;
    approvedRate: number;
    approvedTerm: number;
    buyRate: number | null;
    decisionShown?: boolean;
    isActive?: boolean;
    lenderId: string;
    lenderLogo: string;
    lenderName: string;
    monthlyPayment: number;
};

export type AddCatalogAccessoriesActionPayload = { accessories: CatalogAccessory[] };
export type RemoveCatalogAccessoriesActionPayload = { accessories: CatalogAccessory[] };

export type PaymentFeesTypesState = {
    feesByCategory: FeeCategories;
    feesTypesLoading: boolean;
    feesTypesError: string;
    traceId?: string;
    feesOverrides?: FeeOverride[];
};

export type CustomFeeItem = Omit<paymentServicesTypes.FeeItem, 'feeAmount'> & {
    feeTypeId: number;
    feeDescription: string;
    feeAmount: string;
    isEdited?: boolean;
    isDeleted?: boolean;
};

export type DealerFeeWithIncludeCal = IDealerFee & {
    originalCategory: string;
    isManualFee: boolean;
    isFeeIncludedInCalculation?: boolean;
    includedInCalc?: boolean;
};

export type FinanceDetail = {
    name: string;
    code: number | undefined;
    amount: string;
    category: paymentServicesTypes.FeeCategory;
    isManual?: boolean;
    isEdited?: boolean;
    isDeleted?: boolean;
    includedInCalc?: boolean;
};

export type LeaseDetail = FinanceDetail & {
    upfront: boolean | undefined | null;
};

export type Detail = FinanceDetail | LeaseDetail;

export type GovernmentFeeWithIncludeCal = ItemizedGovernmentFee & {
    includedInCalc?: boolean;
    isFeeIncludedInCalculation?: boolean;
};

export enum TaxCategory {
    UPFRONT = 'upfrontTaxes',
    MONTHLY = 'monthlyUseTaxes',
    ADDITIONAL = 'additionalTaxes',
    CAPITALIZED = 'capitalizedTaxes'
}

export type TaxCategoryTypes = TaxCategory[keyof TaxCategory];

export type TaxDisplayCategories = { [key: string]: paymentServicesTypes.TaxItem[] };

export type CategorizedTaxes = Record<keyof TaxCategoryTypes, paymentServicesTypes.TaxItems>;

export type TaxDetails = {
    [key: string]: any;
};

export interface AioDxgEvent {
    type: 'fsFailure';
    data: AioFSFailure;
    reason?: 'CreateBadRequest' | 'PatchBadRequest' | 'Other';
    errorDetails?: string;
    normalizedErrors?: NormalizedError[];
    retryable?: boolean;
    statusCode?: number;
    dealMethod?: 'POST' | 'PATCH';
    dealRefId?: string;
    attempt?: number;
    maxAttempts?: number;
}
export interface AioFSFailure {
    message?: string;
    code?: string;
    properties?: Record<string, string>[];
}

export type TradeInSlideoutTab = 'trade-in' | 'recon-cost' | 'prior-balance';
